/**
 * Brush cursor preview popover — shown adjacent to palette sliders when
 * the user hovers or drags a brush size/strength/hardness slider.
 *
 * Renders a 2D SVG preview of the brush cursor at its current size and
 * intensity, positioned to the right of the anchor element via a portal.
 * The disc fill uses a radial gradient to faithfully represent brush
 * hardness (edge falloff), matching the 3D cursor's visual language.
 *
 * The component is fully non-interactive (`pointerEvents: none`) so it
 * never steals focus or interferes with slider dragging.
 */

import type { RefObject } from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  COLOR_BORDER_STRONG,
  COLOR_BRUSH_OVERLAY_BG,
  RADIUS_CONTROL,
  Z_POPOVER,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

/** Diameter of the SVG preview area. */
const PREVIEW_SIZE = 72;

/** Gap between anchor edge and popover. */
const GAP = 8;

/** Minimum distance from viewport edges. */
const MARGIN = 8;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BrushCursorPopoverProps {
  /** Element the popover is anchored to. */
  anchorRef: RefObject<HTMLElement | null>;
  /** Whether the popover is visible. */
  open: boolean;
  /** Brush radius in screen-space CSS pixels. */
  radiusPx: number;
  /** Maximum slider value — defines the preview's scale ceiling. */
  maxRadiusPx: number;
  /** Disc fill intensity [0-1] — overall opacity factor of the filled area. */
  intensity: number;
  /** Gradient hardness [0-1]: 1 = hard edge, 0 = fully feathered. */
  hardness: number;
  /** CSS color for the brush preview. */
  color: string;
}

export function BrushCursorPopover({
  anchorRef,
  open,
  radiusPx,
  maxRadiusPx,
  intensity,
  hardness,
  color,
}: BrushCursorPopoverProps) {
  const gradientId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  // Debounce close by one frame to absorb hover/drag transition flicker
  // (pointer capture release momentarily clears hover before re-entering).
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      return;
    }
    const id = requestAnimationFrame(() => setVisible(false));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // ------------------------------------------------------------------
  // Positioning — right of anchor, vertically centred, with fallbacks
  // ------------------------------------------------------------------

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const el = ref.current;
    if (!anchor || !el) return;

    const ar = anchor.getBoundingClientRect();
    const pr = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Default: right of anchor, vertically centred on the anchor
    let left = ar.right + GAP;
    let top = ar.top + (ar.height - pr.height) / 2;

    // Flip left if insufficient space on the right
    if (left + pr.width > vw - MARGIN && ar.left - pr.width - GAP > MARGIN) {
      left = ar.left - pr.width - GAP;
    }

    // Clamp into viewport
    top = Math.max(MARGIN, Math.min(top, vh - MARGIN - pr.height));
    left = Math.max(MARGIN, Math.min(left, vw - MARGIN - pr.width));

    setPos({ top, left });
  }, [anchorRef]);

  // Mount off-screen, then reposition after first frame
  useEffect(() => {
    if (!visible) {
      setPos(null);
      return;
    }
    setPos({ top: -9999, left: -9999 });
  }, [visible]);

  useEffect(() => {
    if (pos?.top === -9999) requestAnimationFrame(reposition);
  }, [pos, reposition]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (!visible || !pos) return null;

  const cx = PREVIEW_SIZE / 2;
  const cy = PREVIEW_SIZE / 2;

  // Map screen-px radius to a proportional visual radius.
  // The preview circle directly represents the brush's screen footprint,
  // scaled so that maxRadiusPx fills 92% of the preview diameter.
  const maxDisplayR = PREVIEW_SIZE * 0.46;
  const minDisplayR = PREVIEW_SIZE * 0.04;
  const norm =
    maxRadiusPx > 0 ? Math.max(0, Math.min(1, radiusPx / maxRadiusPx)) : 0;
  const r = Math.max(minDisplayR, norm * maxDisplayR);

  // Disc fill: radial gradient with hardness-controlled falloff.
  // - hardness=1 → solid disc (first stop at 100%)
  // - hardness=0 → linear fade from centre to edge (first stop at 0%)
  const peakOpacity = Math.max(0.08, intensity * 0.55);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: Z_POPOVER,
        background: COLOR_BRUSH_OVERLAY_BG,
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderRadius: RADIUS_CONTROL,
        border: `0.5px solid ${COLOR_BORDER_STRONG}`,
        padding: 8,
        pointerEvents: "none",
      }}
    >
      <svg
        width={PREVIEW_SIZE}
        height={PREVIEW_SIZE}
        viewBox={`0 0 ${PREVIEW_SIZE} ${PREVIEW_SIZE}`}
      >
        <defs>
          <radialGradient id={gradientId}>
            <stop
              offset={`${hardness * 100}%`}
              stopColor={color}
              stopOpacity={peakOpacity}
            />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </radialGradient>
        </defs>
        {/* Gradient disc — shows hardness falloff */}
        <circle cx={cx} cy={cy} r={r} fill={`url(#${gradientId})`} />
        {/* Ring outline — matches the 3D brush cursor */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          opacity={0.7}
        />
      </svg>
    </div>,
    document.body,
  );
}
